import React, { useCallback, useEffect, useState } from 'react';

import { RouteProp } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';

import { Screen } from '@/components/ScreenComponent';
import WebView from '@/components/Webview';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useArcadeGameResults } from '@/feature/results-arcade/hooks/use-arcade-game-results';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { useRealityCheckStore } from '@/hooks/use-reality-check';
import { useWalletStore } from '@/hooks/use-wallet';
import { RootStackParamList } from '@/navigation/types';
import { LoadingScreen } from '@/screens/LoadingScreen';
import { common, designSystem } from '@/styles/styles';
import { HapticFeedbackTypes, triggerHapticFeedback } from '@/utils/haptic-feedback';
import { logger } from '@/utils/logging';

import { ArcadeGameSearchLoading } from './ArcadeGameSearchLoading';

const HAPTIC_FEEDBACK_MAP = {
    Soft: HapticFeedbackTypes.soft,
    Tiny: HapticFeedbackTypes.impactLight,
    Medium: HapticFeedbackTypes.impactMedium,
    Hard: HapticFeedbackTypes.impactHeavy,
    Success: HapticFeedbackTypes.notificationSuccess,
    Failure: HapticFeedbackTypes.notificationError,
    Light: HapticFeedbackTypes.impactLight,
    Heavy: HapticFeedbackTypes.impactHeavy,
    Selection: HapticFeedbackTypes.selection,
    Rigid: HapticFeedbackTypes.impactHeavy,
    Warning: HapticFeedbackTypes.notificationWarning,
} as const;

const TAG = '[Arcade WebView]';

export const ArcadeGameWebView = ({ route }: { route: RouteProp<RootStackParamList, 'ArcadeGameWebView'> }) => {
    const { iframeUrl, isPractice, betExternalId } = route.params;
    const navigation = useNavigation();
    const [isLoading, setIsLoading] = useState(true);
    usePreventAndroidBackNavigation();
    const { suppressRealityCheck, resumeRealityCheck } = useRealityCheckStore(state => state.actions);
    const { loadUserWallet } = useWalletStore(state => state.actions);

    useEffect(() => {
        setIsLoading(true);
    }, [iframeUrl]);

    const { refetch: refetchGameResults } = useArcadeGameResults({ page: 1 }, false); // Don't auto-fetch results

    const fetchGameResult = useCallback(async () => {
        const { data } = await refetchGameResults();
        if (!data?.pages?.[0]?.data) {
            return;
        }

        if (!betExternalId) {
            return;
        }

        return data.pages[0].data.find(result => result.external_id === betExternalId);
    }, [refetchGameResults, betExternalId]);

    const handleGameEnd = useCallback(async () => {
        if (isPractice) {
            navigation.goBack();
            return;
        }
        try {
            const gameResult = await fetchGameResult();

            if (gameResult) {
                navigation.navigate('ArcadeGameResults', { gameResult });
            } else {
                navigation.goBack();
            }
        } catch (error) {
            logger.debug(TAG, 'Failed to fetch game results:', error);
            navigation.goBack();
        }
    }, [fetchGameResult, navigation, isPractice]);

    // Suppress reality checks during gameplay
    useEffect(() => {
        suppressRealityCheck();
        return () => {
            resumeRealityCheck();
        };
    }, [suppressRealityCheck, resumeRealityCheck]);

    return (
        <Screen edges={['top', 'bottom']} style={{ backgroundColor: designSystem.colors.neutralBlack }}>
            <WebView
                style={{ backgroundColor: ds.colorValue.background.neutral.base }}
                source={{ uri: iframeUrl }}
                originWhitelist={['*']}
                cacheEnabled={false}
                cacheMode={'LOAD_NO_CACHE'}
                mediaPlaybackRequiresUserAction={false}
                allowsInlineMediaPlayback={true}
                injectedJavaScript={`
                    // Listen for SKM custom events
                    window.addEventListener('skm', function(event) {
                        if (window.ReactNativeWebView) {
                            window.ReactNativeWebView.postMessage(JSON.stringify({
                                type: 'windowPostMessage',
                                data: event.detail
                            }));
                        }
                    });
                    
                    true;
                `}
                onMessage={event => {
                    try {
                        const message = JSON.parse(event.nativeEvent.data);

                        // Handle events from game
                        if (message.type === 'windowPostMessage') {
                            const data = JSON.parse(message.data);
                            if (data.method === 'close_game') {
                                handleGameEnd();
                            }
                            if (data.method === 'hide_load_screen' || data.method === 'hide_loading_screen') {
                                setIsLoading(false);
                                loadUserWallet();
                            }
                            if (data.method === 'haptic_feedback') {
                                const hapticType =
                                    HAPTIC_FEEDBACK_MAP[data.payload as keyof typeof HAPTIC_FEEDBACK_MAP];
                                if (hapticType) {
                                    triggerHapticFeedback(hapticType);
                                } else {
                                    // Fallback to medium impact if type not found
                                    triggerHapticFeedback(HapticFeedbackTypes.impactMedium);
                                }
                            }
                        }
                    } catch (error) {
                        logger.debug(TAG, 'Error parsing message from WebView:', error, event.nativeEvent.data);
                    }
                }}
            />
            {isLoading ? (
                <Box position="absolute" top={0} left={0} right={0} bottom={0} style={common.zIndex100}>
                    {isPractice ? <LoadingScreen /> : <ArcadeGameSearchLoading />}
                </Box>
            ) : null}
        </Screen>
    );
};
